/**
 * design.md §P5 — Propose → Plan → Approve → Apply.
 *
 * The most important component in the product. Every schema and layout change
 * passes through exactly this card, whether it came from the agent, the schema
 * admin screen, or a notification. One component, three places, identical.
 */

import { useMutation } from '@tanstack/react-query'
import { AlertTriangle, Check, Clock, Eye, Info, Layers, RotateCcw, ShieldCheck, X } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/form'
import { Badge, Card } from '@/components/ui/surface'
import { api, ApiError } from '@/lib/api'
import type { SchemaChange } from '@/lib/meta'
import { cn, formatDateTime } from '@/lib/utils'
import { canPreview, usePreview } from '@/providers/preview'

const RISK_STYLE = {
  low: { tone: 'success' as const, label: 'Low risk' },
  medium: { tone: 'warn' as const, label: 'Medium risk' },
  high: { tone: 'danger' as const, label: 'High risk' },
}

/** design.md §P5: SQL is always shown, always read-only, always mono. */
export function DdlBlock({ statements, tone }: { statements: string[]; tone?: 'danger' }) {
  if (statements.length === 0) return null
  return (
    <pre
      className={cn(
        'overflow-x-auto rounded-md border px-3 py-2 font-mono text-[11.5px] leading-5',
        tone === 'danger'
          ? 'border-danger/30 bg-danger-soft text-danger'
          : 'border-border-soft bg-surface-2 text-muted',
      )}
    >
      {statements.join('\n')}
    </pre>
  )
}

function Fact({
  label,
  value,
  tone,
}: {
  label: string
  value: React.ReactNode
  tone?: 'good' | 'bad'
}) {
  return (
    <div>
      <dt className="text-[10px] font-semibold tracking-wider text-faint uppercase">{label}</dt>
      <dd
        className={cn(
          'mt-0.5 text-[13px] font-medium',
          tone === 'good' && 'text-success',
          tone === 'bad' && 'text-danger',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

export function PlanCard({
  plan,
  onSettled,
  compact,
}: {
  plan: SchemaChange
  onSettled?: (plan: SchemaChange) => void
  compact?: boolean
}) {
  const [confirmText, setConfirmText] = React.useState('')
  const [error, setError] = React.useState<string | null>(plan.error ?? null)

  // The server says "expired"; a plan left open past its time turns so here too.
  const expired =
    plan.status === 'expired' ||
    (plan.status === 'planned' && new Date(plan.expires_at) < new Date())
  const pending = plan.status === 'planned' && !expired

  const preview = usePreview()
  const previewing = preview.preview?.plan.id === plan.id

  const act = useMutation({
    mutationFn: (action: 'apply' | 'reject') =>
      api.post<SchemaChange>(`/api/meta/changes/${plan.id}/${action}`),
    onSuccess: (updated) => {
      setError(null)
      if (previewing) preview.stop()
      onSettled?.(updated)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not apply.'),
  })

  // An expired plan, worked out again against today's schema: a new plan to review.
  const replan = useMutation({
    mutationFn: () => api.post<SchemaChange>(`/api/meta/changes/${plan.id}/replan`),
    onSuccess: (fresh) => {
      setError(null)
      onSettled?.(fresh)
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not plan it again.'),
  })

  // design.md §P5: medium/high require typing the table name, because the
  // server will refuse an unreviewed change anyway and the UI must not imply
  // this is a casual click.
  const needsTyping = plan.risk !== 'low' && !plan.layout_only
  // What the approver types: the first table the plan touches, or for a plan that
  // creates an app (an import), the app's slug.
  const subject = String(
    plan.diff.find((entry) => entry.table)?.table ??
      plan.diff.find((entry) => entry.app)?.app ??
      plan.diff.find((entry) => entry.workflow)?.workflow ??
      plan.intent.slice(0, 24),
  )
  const canApply = pending && (!needsTyping || confirmText.trim() === subject)

  const risk = RISK_STYLE[plan.risk]

  return (
    <Card className={cn('overflow-hidden', compact && 'text-[13px]')}>
      <div className="border-b border-border-soft px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[13.5px] font-medium">{plan.intent}</p>
            {plan.via ? <p className="text-[11px] text-faint">Proposed by {plan.via}</p> : null}
          </div>
          {plan.status !== 'planned' ? (
            <Badge
              tone={
                plan.status === 'applied'
                  ? 'success'
                  : plan.status === 'failed'
                    ? 'danger'
                    : 'neutral'
              }
            >
              {plan.status}
            </Badge>
          ) : expired ? (
            <Badge tone="neutral">
              <Clock className="size-3" />
              expired
            </Badge>
          ) : null}
        </div>

        {/* The four facts design.md §P5 puts above the fold. A layout change is
            deliberately NOT risk-badged — conflating it with a schema change
            teaches people to click through real warnings. */}
        {plan.layout_only ? (
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted">
            <Layers className="size-3.5 text-accent" />
            Layout only — no data or schema changes.
          </p>
        ) : (
          <dl className="mt-2.5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Fact label="Risk" value={<Badge tone={risk.tone}>{risk.label}</Badge>} />
            <Fact
              label="Reversible"
              value={plan.reversible ? 'Yes' : 'No'}
              tone={plan.reversible ? 'good' : 'bad'}
            />
            <Fact label="Rows affected" value={<span className="tabular">{plan.affected_rows}</span>} />
            <Fact
              label="Existing data"
              value={plan.touches_existing ? 'Touched' : 'Untouched'}
              tone={plan.touches_existing ? 'bad' : 'good'}
            />
          </dl>
        )}
      </div>

      <div className="space-y-3 px-4 py-3">
        {plan.diff.length > 0 ? (
          <section>
            <h4 className="mb-1.5 text-[10px] font-semibold tracking-wider text-faint uppercase">
              {plan.layout_only ? 'View change' : 'Metadata'}
            </h4>
            <ul className="space-y-1">
              {plan.diff.map((entry, index) => (
                <li key={index} className="rounded-md bg-surface-2 px-2.5 py-1.5 text-xs">
                  <span className="font-mono text-accent">{String(entry.kind)}</span>{' '}
                  <span className="text-muted">
                    {[
                      entry.app,
                      entry.table,
                      entry.field,
                      entry.view,
                      entry.workflow,
                      entry.role,
                      entry.permission,
                    ]
                      .filter(Boolean)
                      .map(String)
                      .join(' · ')}
                  </span>
                  {entry.note ? (
                    <span className="mt-0.5 block text-[11px] text-faint">
                      {String(entry.note)}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {plan.ddl.length > 0 ? (
          <section>
            <h4 className="mb-1.5 text-[10px] font-semibold tracking-wider text-faint uppercase">
              SQL that will run
            </h4>
            <DdlBlock statements={plan.ddl} />
          </section>
        ) : null}

        {plan.backfill_sql.length > 0 ? (
          <section>
            <h4 className="mb-1.5 text-[10px] font-semibold tracking-wider text-faint uppercase">
              Backfill
            </h4>
            <DdlBlock statements={plan.backfill_sql} />
          </section>
        ) : null}

        {/* design.md §P5: high risk shows the rows that cannot convert, inline,
            before the decision. */}
        {plan.unconvertible.length > 0 ? (
          <section className="rounded-md border border-danger/30 bg-danger-soft/50 px-3 py-2">
            <h4 className="flex items-center gap-1.5 text-xs font-medium text-danger">
              <AlertTriangle className="size-3.5" />
              Values that cannot be converted
            </h4>
            {plan.unconvertible.map((item) => (
              <div key={`${item.table}.${item.field}`} className="mt-1.5">
                <p className="text-[11px] text-muted">
                  <span className="tabular font-medium">{item.count}</span> row(s) in{' '}
                  <span className="font-mono">{item.field}</span>. They will be left empty in the
                  new column; the original column keeps every value.
                </p>
                <ul className="mt-1 flex flex-wrap gap-1">
                  {item.sample.map((sample) => (
                    <li
                      key={sample.id}
                      className="rounded bg-surface px-1.5 py-0.5 font-mono text-[11px] text-danger"
                    >
                      {sample.value}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        ) : null}

        {!plan.layout_only && plan.ddl.length === 0 && plan.diff.length > 0 ? (
          <p className="flex items-start gap-1.5 text-[11px] text-muted">
            <ShieldCheck className="mt-px size-3.5 shrink-0 text-success" />
            Metadata only — no SQL runs and no data is touched.
          </p>
        ) : null}

        {!plan.layout_only && plan.ddl.length > 0 ? (
          <p className="flex items-start gap-1.5 text-[11px] text-muted">
            <Info className="mt-px size-3.5 shrink-0" />
            Additive only. No existing column is renamed, narrowed or removed.
          </p>
        ) : null}

        {error ? (
          <div className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2">
            <p className="text-xs font-medium text-danger">The change did not apply</p>
            <DdlBlock statements={[error]} tone="danger" />
          </div>
        ) : null}

        {plan.status === 'applied' && plan.applied_at ? (
          <p className="text-[11px] text-faint">Applied {formatDateTime(plan.applied_at)}</p>
        ) : null}
      </div>

      {pending ? (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border-soft bg-surface-2/40 px-4 py-2.5">
          {needsTyping ? (
            <label className="mr-auto flex items-center gap-2 text-[11px] text-muted">
              Type <span className="font-mono text-text">{subject}</span> to confirm
              <Input
                value={confirmText}
                onChange={(event) => setConfirmText(event.target.value)}
                className="h-7 w-44 text-xs"
                aria-label={`Type ${subject} to confirm`}
              />
            </label>
          ) : null}
          {canPreview(plan) ? (
            // design.md §P8: see a layout change on the live screen before owning it.
            <Button
              variant="secondary"
              size="sm"
              className={needsTyping ? undefined : 'mr-auto'}
              onClick={() => preview.start(plan)}
              aria-pressed={previewing}
            >
              <Eye />
              {previewing ? 'Previewing' : 'Preview'}
            </Button>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => act.mutate('reject')}
            loading={act.isPending && act.variables === 'reject'}
          >
            <X />
            Reject
          </Button>
          <Button
            // High and medium risk get a danger button, not a primary one.
            variant={plan.risk === 'low' || plan.layout_only ? 'primary' : 'danger'}
            size="sm"
            disabled={!canApply}
            loading={act.isPending && act.variables === 'apply'}
            onClick={() => act.mutate('apply')}
          >
            <Check />
            Approve &amp; apply
          </Button>
        </div>
      ) : expired ? (
        <div className="flex items-center justify-between gap-3 border-t border-border-soft bg-surface-2/40 px-4 py-2.5">
          <p className="text-[11px] text-muted">
            This plan expired: the schema may have changed since it was made. Plan it again
            to review the change as it would run now.
          </p>
          <div className="flex shrink-0 gap-1.5">
            {/* Not wanted any more: close it, so it reads "rejected" in the history. */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => act.mutate('reject')}
              loading={act.isPending && act.variables === 'reject'}
            >
              <X />
              Reject
            </Button>
            <Button size="sm" variant="secondary" loading={replan.isPending} onClick={() => replan.mutate()}>
              <RotateCcw />
              Plan again
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  )
}
